/**
 * Emergent Marketplace — Home page (composition layer).
 * Architectural Layer: Presentation
 *
 * Refactored in Session 30 (PD-2). What used to be a 985-line monolith is
 * now a thin composition that wires together:
 *   - useMarketplaceData()             → data loading + paging + search state
 *   - <VendorCard>, <ProductCard>      → /components/marketplace/
 *   - <MarketplacePagination>          → /components/marketplace/
 *   - MarketplaceHome.css              → sibling stylesheet
 *
 * Design: harmonized with main app — transparent glass blocks, teal
 * accents, cosmic background. Fonts: Cormorant Garamond (display) +
 * Nunito Sans (body).
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingBag, Calendar, Store, ChevronRight, ChevronLeft, Search, Heart, History,
} from "lucide-react";
import { apiFetch } from "./api";
import OrderHistory from "./OrderHistory";
import { MarketplaceHeader } from "@/components/marketplace/MarketplaceHeader";
import { AddedToCartOverlay } from "@/components/marketplace/AddedToCartOverlay";
import { VendorCard } from "../../components/marketplace/VendorCard";
import { ProductCard } from "../../components/marketplace/ProductCard";
import { MarketplacePagination } from "../../components/marketplace/MarketplacePagination";
import { HotDealsRail } from "../../components/marketplace/HotDealsRail";
import { ShelfRail } from "../../components/marketplace/ShelfRail";
import { SellHereModal } from "../../components/marketplace/SellHereModal";
import { SpotlightSlot } from "../../components/spotlights/SpotlightSlot";
import { useMarketplaceData } from "../../hooks/useMarketplaceData";
import { useLanguage, TranslatableText } from "../../contexts/LanguageContext";
import "./MarketplaceHome.css";

const SECTIONS = [
  { id: "explore", label: "Explore", icon: Store },
  { id: "recent", label: "Recently Viewed", icon: History },
  { id: "orders", label: "My Orders", icon: ShoppingBag },
];

const SORT_OPTIONS = [
  { value: "mix", label: "Fresh mix" },
  { value: "newest", label: "Newest first" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "rating", label: "Best rated" },
  { value: "name", label: "Name A–Z" },
];

export default function MarketplaceHome({ onNavigate }) {
  const navigate = useNavigate();

  const {
    vendors, products, shelves,
    page, totalPages, totalProducts, activeShelf, activeShelfName,
    search, searchDebounced, loading,
    sort, minPrice, maxPrice,
    setSearch, setActiveShelf, setPage,
    setSort, setMinPrice, setMaxPrice,
  } = useMarketplaceData();

  const [activeSection, setActiveSection] = useState("explore");

  // ── Cart in place (session 62d) ──────────────────────────────────────
  // Adding used to mean a trip into the product page; the only feedback was a 2.5s label.
  const [cartLines, setCartLines] = useState([]);
  const [addedProduct, setAddedProduct] = useState(null);
  const [sellHereOpen, setSellHereOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const loadCartLines = () => {
    apiFetch("/cart")
      .then((c) => setCartLines(c.items || []))
      .catch(() => setCartLines([]));
  };
  useEffect(() => { loadCartLines(); }, []);

  const qtyInCart = (id) => (cartLines.find((i) => i.product_id === id)?.qty || 0);

  const addToCart = async (product) => {
    try {
      await apiFetch("/cart/items", {
        method: "POST", body: JSON.stringify({ product_id: product.id, qty: 1 }),
      });
      loadCartLines();
      window.dispatchEvent(new Event("cart-updated"));
      setAddedProduct(product);
    } catch (e) {
      // A product sold in types has to be picked on its own page
      toast.error(e.message || "Could not add that to your basket");
    }
  };

  const changeQty = async (product, qty) => {
    try {
      if (qty <= 0) await apiFetch(`/cart/items/${product.id}`, { method: "DELETE" });
      else await apiFetch(`/cart/items/${product.id}`, { method: "PATCH", body: JSON.stringify({ qty }) });
      loadCartLines();
      window.dispatchEvent(new Event("cart-updated"));
    } catch (e) { /* ignore */ }
  };

  const loadRecent = () => {
    setRecentLoading(true);
    apiFetch("/marketplace/recently-viewed")
      .then((d) => setRecent(d.products || []))
      .catch(() => setRecent([]))
      .finally(() => setRecentLoading(false));
  };

  // ── Dynamic content translation (Session 37 — global language) ────────
  const { language, translateContent } = useLanguage();
  const [titleMap, setTitleMap] = useState({});
  useEffect(() => {
    const titles = [...products, ...savedProducts].map((p) => p.title).filter(Boolean);
    if (language === "en" || titles.length === 0) { setTitleMap({}); return; }
    let active = true;
    translateContent(titles).then((m) => { if (active) setTitleMap(m); });
    return () => { active = false; };
  }, [products, language]);

  // ── Wishlist / Saved items (Session 36) ──────────────────────────────
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [savedView, setSavedView] = useState(false);
  const [savedProducts, setSavedProducts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    if (!isLoggedIn) return;
    apiFetch("/wishlist/ids")
      .then((d) => setSavedIds(new Set(d.product_ids || [])))
      .catch(() => {});
  }, [isLoggedIn]);

  const loadSavedProducts = () => {
    setSavedLoading(true);
    apiFetch("/wishlist")
      .then((d) => setSavedProducts(d.items || []))
      .catch(() => setSavedProducts([]))
      .finally(() => setSavedLoading(false));
  };

  const toggleSave = async (product) => {
    if (!isLoggedIn) return;
    const id = product.id;
    const wasSaved = savedIds.has(id);
    setSavedIds((prev) => {
      const next = new Set(prev);
      wasSaved ? next.delete(id) : next.add(id);
      return next;
    });
    try {
      await apiFetch(`/wishlist/${id}`, { method: wasSaved ? "DELETE" : "POST" });
      if (savedView) {
        if (wasSaved) setSavedProducts((p) => p.filter((x) => x.id !== id));
        else loadSavedProducts();
      }
    } catch {
      // revert on failure
      setSavedIds((prev) => {
        const next = new Set(prev);
        wasSaved ? next.add(id) : next.delete(id);
        return next;
      });
    }
  };

  const openSavedView = () => {
    setSavedView(true);
    loadSavedProducts();
  };

  // ── Vendor rail (marquee on desktop, manual scroll on mobile) ────────
  const vendorScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  // Track viewport for marquee vs manual-scroll mode
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Randomized vendor order per session (Fisher-Yates) — new shuffle on each mount
  const shuffledVendors = useMemo(() => {
    const arr = [...vendors];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [vendors]);

  // Marquee timing: scale duration by vendor count; random starting offset
  const marqueeStyle = useMemo(() => {
    const n = shuffledVendors.length || 1;
    const duration = Math.max(30, n * 5); // ~5s per card, 30s floor
    const delay = -(Math.random() * duration); // randomized starting point
    return { "--mp-marquee-duration": `${duration}s`, "--mp-marquee-delay": `${delay}s` };
  }, [shuffledVendors.length]);

  // Vendor scroll arrow visibility (mobile only)
  useEffect(() => {
    if (!isMobile) return;
    const checkScrollButtons = () => {
      const el = vendorScrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 10);
      setCanScrollRight(el.scrollWidth > el.clientWidth + 10);
    };
    const timer = setTimeout(checkScrollButtons, 200);
    const el = vendorScrollRef.current;
    if (el) el.addEventListener("scroll", checkScrollButtons);
    window.addEventListener("resize", checkScrollButtons);
    return () => {
      clearTimeout(timer);
      if (el) el.removeEventListener("scroll", checkScrollButtons);
      window.removeEventListener("resize", checkScrollButtons);
    };
  }, [shuffledVendors, isMobile]);

  const scrollVendors = (dir) => {
    const el = vendorScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 260, behavior: "smooth" });
  };

  const goToStorefront = (v) =>
    navigate(`/dashboard/marketplace/store/${v.store_slug || v.id}`);

  const goToReturns = (v) =>
    navigate(`/dashboard/marketplace/store/${v.store_slug || v.id}?tab=returns`);

  // A shelf tile opens that shelf's own page — clicking has to visibly go somewhere.
  const pickShelf = (slug) => {
    if (slug === "all") {
      setSavedView(false);
      setActiveShelf("all");
      navigate("/dashboard/marketplace", { replace: true });
      return;
    }
    navigate(`/dashboard/marketplace/shelf/${slug}`);
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="mp-root">
      {/* One header on every marketplace page — search, categories, saved, cart */}
      <MarketplaceHeader
        search={search}
        onSearch={setSearch}
        showShelfPills={false}
        savedCount={savedIds.size}
        onSaved={() => { setActiveSection("explore"); openSavedView(); }}
      />
      <div className="mp-container">

        {/* Header */}
        <div className="mp-header">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <p className="mp-title">Marketplace</p>
              <p className="mp-subtitle">Sacred Commerce</p>
            </div>
            <button
              className="mp-sell-btn"
              onClick={() => setSellHereOpen(true)}
              data-testid="marketplace-sell-btn"
            >
              Sell Here
            </button>
          </div>
        </div>

        {/* Section Nav */}
        <div className="mp-nav" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 4 }}>
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`mp-nav-btn ${activeSection === id ? "active" : ""}`}
                onClick={() => {
                  setActiveSection(id);
                  setSavedView(false);
                  if (id === "recent") loadRecent();
                }}
                data-testid={`mp-nav-${id}`}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {activeSection === "explore" && (
          <>
            {/* Hero — flanked by Spotlight blocks on large screens */}
            <div className="flex items-stretch gap-4 justify-center">
              <SpotlightSlot slot="marketplace_flank" variant="flank" pick={0} className="hidden lg:flex w-56" />
              <div className="mp-hero flex-1">
                <h2>Tools For Your<br /><em>Awakening Journey</em></h2>
                <p>Discover offerings to help on your spiritual consciousness journey.</p>
              </div>
              <SpotlightSlot slot="marketplace_flank" variant="flank" pick={1} className="hidden lg:flex w-56" />
            </div>

            {/* Spotlight banner under the hero */}
            <SpotlightSlot slot="marketplace_banner" variant="banner" className="my-4" />

            {/* Hot Deals — vendors running active promotions */}
            <HotDealsRail onSelect={goToStorefront} />

            {/* Vendors */}
            <div className="mp-section">
              <div className="mp-section-header">
                <span className="mp-section-title">Vendors</span>
                <button
                  className="mp-see-all"
                  onClick={() => navigate("/dashboard/marketplace/vendors")}
                  data-testid="vendors-see-all"
                >
                  See all <ChevronRight size={12} />
                </button>
              </div>
              <div className="mp-vendor-scroll-wrap">
                {isMobile ? (
                  <>
                    {canScrollLeft && (
                      <button
                        className="mp-scroll-btn left"
                        onClick={() => scrollVendors(-1)}
                        data-testid="vendor-scroll-left"
                      >
                        <ChevronLeft size={18} />
                      </button>
                    )}
                    <div
                      className={`mp-vendor-scroll ${!canScrollRight ? "no-fade" : ""}`}
                      ref={vendorScrollRef}
                      data-testid="vendor-manual-scroll"
                    >
                      {shuffledVendors.map((v) => (
                        <VendorCard key={v.id} vendor={v} onClick={() => goToStorefront(v)} onReturns={() => goToReturns(v)} />
                      ))}
                    </div>
                    {canScrollRight && (
                      <button
                        className="mp-scroll-btn right"
                        onClick={() => scrollVendors(1)}
                        data-testid="vendor-scroll-right"
                      >
                        <ChevronRight size={18} />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="mp-vendor-marquee" style={marqueeStyle} data-testid="vendor-marquee">
                    <div className="mp-vendor-marquee-track" data-testid="vendor-marquee-track">
                      {/* Render twice for seamless infinite loop */}
                      {[...shuffledVendors, ...shuffledVendors].map((v, idx) => (
                        <VendorCard
                          key={`${v.id}-${idx < shuffledVendors.length ? "a" : "b"}`}
                          vendor={v}
                          onClick={() => goToStorefront(v)}
                          onReturns={() => goToReturns(v)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mp-divider" />

            {/* Shelves sit directly above the products, with the sort bar under them */}
            <ShelfRail
              shelves={shelves}
              active={activeShelf}
              onSelect={pickShelf}
            />

            {/* Sort + price filter */}
            <div className="mp-sortbar" data-testid="mp-sortbar">
              <label htmlFor="mp-sort">Sort</label>
              <select id="mp-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                data-testid="mp-sort-select">
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <label htmlFor="mp-min">Price</label>
              <input id="mp-min" type="number" min="0" placeholder="Min" value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)} data-testid="mp-min-price" />
              <span style={{ color: "rgba(245,240,232,0.35)" }}>–</span>
              <input type="number" min="0" placeholder="Max" value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)} data-testid="mp-max-price" />
              {(minPrice || maxPrice || sort !== "mix") && (
                <button className="mp-nav-btn" onClick={() => { setSort("mix"); setMinPrice(""); setMaxPrice(""); }}
                  data-testid="mp-clear-filters">Clear</button>
              )}
              <span className="mp-sortbar-count">{totalProducts} product{totalProducts !== 1 ? "s" : ""}</span>
            </div>

            {/* Products */}
            <div className="mp-section">
              <div className="mp-section-header">
                <span className="mp-section-title">
                  {savedView
                    ? <TranslatableText text="Saved Items" />
                    : (activeShelfName
                        ? <TranslatableText text={activeShelfName} />
                        : <TranslatableText text="All Products" />)}
                  <span style={{ fontSize: 12, color: "rgba(245,240,232,0.3)", marginLeft: 8, fontWeight: 400, fontFamily: "sans-serif" }}>
                    {savedView
                      ? `${savedProducts.length} saved`
                      : `${totalProducts} product${totalProducts !== 1 ? "s" : ""}`}
                  </span>
                </span>
                {isLoggedIn && (
                  <button
                    type="button"
                    className="mp-saved-tab"
                    data-testid="saved-items-toggle"
                    onClick={() => (savedView ? setSavedView(false) : openSavedView())}
                  >
                    <Heart size={14} fill={savedView ? "currentColor" : "none"} />
                    {savedView ? <TranslatableText text="Back to Browse" /> : <><TranslatableText text="Saved" />{savedIds.size ? ` (${savedIds.size})` : ""}</>}
                  </button>
                )}
              </div>

              {savedView ? (
                savedLoading ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}>
                    <TranslatableText text="Loading saved items..." />
                  </div>
                ) : savedProducts.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }} data-testid="saved-items-empty">
                    <TranslatableText text="No saved items yet. Tap the heart on a product to save it for later." />
                  </div>
                ) : (
                  <div className="mp-product-grid">
                    {savedProducts.map((p) => (
                      <ProductCard
                        key={p.id}
                        product={p}
                        displayTitle={titleMap[p.title]}
                        saved={savedIds.has(p.id)}
                        onToggleSave={toggleSave}
                        cartQty={qtyInCart(p.id)}
                        onAdd={addToCart}
                        onQty={changeQty}
                        onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)}
                      />
                    ))}
                  </div>
                )
              ) : loading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}>
                  <TranslatableText text="Loading products..." />
                </div>
              ) : products.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}>
                  <TranslatableText text="No products found." />{searchDebounced ? ` "${searchDebounced}"` : ""}
                </div>
              ) : (
                <div className="mp-product-grid">
                  {products.flatMap((p, i) => {
                    const nodes = [
                      <ProductCard
                        key={p.id}
                        product={p}
                        displayTitle={titleMap[p.title]}
                        saved={savedIds.has(p.id)}
                        onToggleSave={isLoggedIn ? toggleSave : undefined}
                        cartQty={qtyInCart(p.id)}
                        onAdd={isLoggedIn ? addToCart : undefined}
                        onQty={isLoggedIn ? changeQty : undefined}
                        onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)}
                      />,
                    ];
                    if ((i + 1) % 6 === 0) {
                      nodes.push(
                        <SpotlightSlot
                          key={`sp-${i}`}
                          slot="marketplace_interleave"
                          variant="card"
                          pick={Math.floor(i / 6)}
                        />
                      );
                    }
                    return nodes;
                  })}
                </div>
              )}

              {!savedView && (
                <MarketplacePagination
                  page={page}
                  totalPages={totalPages}
                  onChange={setPage}
                />
              )}
            </div>

            <div style={{ height: 24 }} />
          </>
        )}

        {activeSection === "recent" && (
          <div className="mp-section" data-testid="marketplace-recent-section">
            <div className="mp-section-header">
              <span className="mp-section-title">Recently viewed</span>
            </div>
            {recentLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}>
                Loading…
              </div>
            ) : recent.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: "rgba(245,240,232,0.4)" }}
                data-testid="marketplace-recent-empty">
                Nothing here yet. Products you open will appear here so you can find them again.
              </div>
            ) : (
              <div className="mp-product-grid">
                {recent.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    displayTitle={titleMap[p.title]}
                    saved={savedIds.has(p.id)}
                    onToggleSave={isLoggedIn ? toggleSave : undefined}
                    cartQty={qtyInCart(p.id)}
                    onAdd={isLoggedIn ? addToCart : undefined}
                    onQty={isLoggedIn ? changeQty : undefined}
                    onClick={() => navigate(`/dashboard/marketplace/product/${p.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === "orders" && (
          <div data-testid="marketplace-orders-section">
            <OrderHistory onNavigateToMarketplace={() => setActiveSection("explore")} />
          </div>
        )}

      </div>

      {/* Added to cart — confirmation, the whole cart, and more to browse underneath */}
      {addedProduct && (
        <AddedToCartOverlay
          product={addedProduct}
          onClose={() => { setAddedProduct(null); loadCartLines(); }}
        />
      )}

      <SellHereModal
        open={sellHereOpen}
        onClose={() => setSellHereOpen(false)}
        currentUser={JSON.parse(localStorage.getItem("user") || "null")}
      />

    </div>
  );
}
